 import {
  animate,
  Component,
  Input,
  state,
  style,
  transition,
  trigger,
  ElementRef,
  AfterViewInit,
  HostListener
} from '@angular/core';

import { SkyModalHostService } from './modal-host.service';
import { SkyModalConfiguration } from './modal-configuration';

import { SkyModalComponentAdapterService } from './modal-component-adapter.service';
import { SkyWindowRefService } from '../window';

let skyModalUniqueIdentifier: number = 0;


export class SkyModalComponent implements AfterViewInit {
  public modalState = 'in';
  public modalContentId: string = 'sky-modal-content-id-' + skyModalUniqueIdentifier.toString();
  public modalHeaderId: string = 'sky-modal-header-id-' + skyModalUniqueIdentifier.toString();

  
  public set tiledBody(value: boolean) {
    this.config.tiledBody = value;
  }

  public get modalZIndex() {
    return this.hostService.getModalZIndex();
  }

  public get modalFullPage() {
    return this.config.fullPage;
  }

  public get isSmallSize() {
    return !this.modalFullPage && this.isSizeEqual(this.config.size, 'small');
  }

  public get isMediumSize() {
    return !this.modalFullPage && !(this.isSmallSize || this.isLargeSize);
  }

  public get isLargeSize() {
    return !this.modalFullPage && this.isSizeEqual(this.config.size, 'large');
  }

  public get isTiledBody() {
    return this.config.tiledBody;
  }

  public get ariaDescribedBy() {
    return this.config.ariaDescribedBy || this.modalContentId;
  }

  public get ariaLabelledBy() {
    return this.config.ariaLabelledBy || this.modalHeaderId;
  }

  public get helpKey() {
    return this.config.helpKey;
  }

  constructor(
    private hostService: SkyModalHostService,
    private config: SkyModalConfiguration,
    private elRef: ElementRef,
    private windowRef: SkyWindowRefService,
    private componentAdapter: SkyModalComponentAdapterService
  ) { }

  
  public onDocumentKeyDown(event: KeyboardEvent) {
    /* istanbul ignore else */
    /* sanity check */
    if (SkyModalHostService.openModalCount > 0) {
      let topModal = SkyModalHostService.topModal;
      if (topModal && topModal === this.hostService) {
        switch (event.which) {
          case 27: { // Esc key pressed
            event.preventDefault();
            this.hostService.onClose();
            break;
          }

          case 9: {  // Tab pressed
            let focusChanged = false;

            let focusElementList = this.componentAdapter.loadFocusElementList(this.elRef);

            if (
              event.shiftKey &&
              (this.componentAdapter.isFocusInFirstItem(event, focusElementList) ||
              this.componentAdapter.isModalFocused(event, this.elRef))) {

              focusChanged = this.componentAdapter.focusLastElement(focusElementList);
            } else if (this.componentAdapter.isFocusInLastItem(event, focusElementList)) {
              focusChanged = this.componentAdapter.focusFirstElement(focusElementList);
            }

            if (focusChanged) {
              event.preventDefault();
              event.stopPropagation();
            }
            break;
          }

          default:
            break;
        }
      }

    }
  }

  public ngAfterViewInit() {
    skyModalUniqueIdentifier++;
    this.componentAdapter.handleWindowChange(this.elRef);

    // Adding a timeout to avoid ExpressionChangedAfterItHasBeenCheckedError.
    // https://stackoverflow.com/questions/40562845
    this.windowRef.getWindow().setTimeout(() => {
      this.componentAdapter.modalOpened(this.elRef);
    });
  }

  public helpButtonClick() {
    this.hostService.onOpenHelp(this.helpKey);
  }

  public closeButtonClick() {
    this.hostService.onClose();
  }

  public windowResize() {
    this.componentAdapter.handleWindowChange(this.elRef);
  }

  private isSizeEqual(actualSize: string, size: string) {
    return actualSize && actualSize.toLowerCase() === size;
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'sky-modal',
  template: `<!--
  Animations are broken in Chrome v52. Angular 2 RC5 will fix it.
  https://github.com/angular/angular/issues/10245
-->
<!--<div @modalState="modalState">-->

<div
  class="sky-modal-dialog"
  role="dialog"
  [attr.aria-describedby]="ariaDescribedBy"
  [attr.aria-labelledby]="ariaLabelledBy"
  (window:resize)="windowResize()"
>
  <div class="sky-modal sky-shadow"
    tabindex="-1"
    [ngClass]="{
      'sky-modal-full-page': modalFullPage,
      'sky-modal-small' : isSmallSize,
      'sky-modal-medium' : isMediumSize,
      'sky-modal-large' : isLargeSize,
      'sky-modal-tiled' : isTiledBody
      }"
    [ngStyle]="{
        zIndex: modalZIndex
      }">

    <div class="sky-modal-header" [hidden]="!headerContent || !headerContent.children || headerContent.children.length < 1">
      <div [attr.id]="modalHeaderId" class="sky-modal-header-content"  #headerContent>
        <ng-content select="sky-modal-header"></ng-content>
      </div>
      <div class="sky-modal-header-buttons">
        <button *ngIf="helpKey" type="button" class="sky-btn" name="help-button" [attr.aria-label]="'open_help' | skyResources" (click)="helpButtonClick()">
          <i class="fa fa-question-circle"></i>
        </button>

        <button type="button" class="sky-btn sky-modal-btn-close" [attr.aria-label]="'modal_close' | skyResources" (click)="closeButtonClick()">

          <i class="fa fa-close"></i>
        </button>
      </div>

    </div>
    <div [attr.id]="modalContentId" class="sky-modal-content">
      <ng-content select="sky-modal-content"></ng-content>
    </div>
    <div class="sky-modal-footer">
      <ng-content select="sky-modal-footer"></ng-content>
    </div>
</div>
</div>
`,
  styles: [`.sky-modal {
  border-top: 1px solid #cdcfd2;
  border-bottom: 1px solid #cdcfd2;
  border-left: 1px solid #cdcfd2;
  border-right: 1px solid #cdcfd2;
  position: fixed;
  width: auto;
  left: 0;
  right: 0;
  top: 20px;
  margin: 10px;
  display: flex;
  flex-direction: column;
}

.sky-modal:focus {
  outline: none;
}

@media (min-width: 768px) {
  .sky-modal:not(.sky-modal-large) {
    margin: 0 auto;
  }
  .sky-modal-small {
    width: 300px;
  }
  .sky-modal-medium {
    width: 600px;
  }
}

@media (min-width: 920px) {
  .sky-modal-large {
    margin: 0 auto;
    width: 900px;
  }
}

.sky-modal-content {
  background-color: #fff;
  padding: 15px;
}

.sky-modal-tiled .sky-modal-content {
  background-color: #eeeeef;
}

.sky-modal-tiled .sky-modal-content /deep/ .sky-tile-title {
  font-family: "Blackbaud Sans", "Helvetica Neue", Arial, sans-serif;
  color: #686c73;
  font-weight: 300;
  font-size: 19px;
}

.sky-modal-tiled .sky-modal-content /deep/ .sky-tile-tools .sky-chevron {
  margin: 9px 9px 8px 0;
}

.sky-modal-header {
  /* Need less padding because of the close button padding */
  padding-left: 15px;
  padding-top: 9px;
  padding-bottom: 9px;
  padding-right: 3px;
  background-color: #fff;
  display: flex;
  align-items: baseline;
  border-bottom: 1px solid #e2e3e4;
}

.sky-modal-header-buttons .sky-btn {
  border: none;
  color: #cdcfd2;
  cursor: pointer;
}

.sky-modal-header-buttons .sky-btn:hover {
  color: #979ba2;
  transition: color 150ms;
}

.sky-modal-header-content {
  flex-grow: 1;
  font-family: "Blackbaud Sans", "Helvetica Neue", Arial, sans-serif;
  color: #282b31;
  font-weight: 600;
  font-size: 16px;
}

.sky-modal-header {
  flex-shrink: 0;
}

.sky-modal-content {
  overflow-y: auto;
}

.sky-modal-footer {
  flex-shrink: 0;
}

.sky-modal-footer /deep/ sky-tabset-nav-button + sky-tabset-nav-button {
  margin-left: 10px;
}

.sky-modal-footer /deep/ sky-tabset-nav-button + .sky-btn {
  margin-left: 10px;
}

.sky-modal-footer /deep/ .sky-btn + .sky-btn {
  margin-left: 10px;
}

.sky-modal-footer /deep/ .sky-btn + .sky-btn-link {
  margin-left: -2px;
  /* offset the padding of the btn-link */
}

.sky-modal-full-page {
  width: 100%;
  top: 0;
  margin: 0;
}

.sky-modal-full-page .sky-modal-header-content {
  font-family: "Blackbaud Sans Condensed", "Helvetica Neue Condensed", "Arial Narrow";
  color: #282b31;
  font-weight: 300;
  font-size: 26px;
}

.sky-modal-full-page .sky-modal-header-buttons .fa-close {
  font-size: 20px;
}

.sky-modal-full-page .sky-modal-content {
  flex-grow: 1;
}

:host /deep/ .sky-sectioned-form {
  height: 460px;
  margin: -15px;
}
`],
  animations: [
    trigger('modalState', [
      state('in', style({ opacity: '1.0' })),
      state('out', style({ opacity: '0.0' })),
      transition('void => *', [
        style({ opacity: '0.0' }),
        animate(150)
      ]),
      transition('* => void', [
        animate(150, style({ opacity: '0.0' }))
      ])
    ])
  ],
  providers: [
    SkyModalComponentAdapterService
  ]
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: SkyModalHostService, },
{type: SkyModalConfiguration, },
{type: ElementRef, },
{type: SkyWindowRefService, },
{type: SkyModalComponentAdapterService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'tiledBody': [{ type: Input },],
'onDocumentKeyDown': [{ type: HostListener, args: ['document:keydown', ['$event'], ] },],
};
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
